import axios from 'axios';
import apiHmacService from './apiHmacService';

const ACCOUNT_LOOKUP_BASE_URL =
  'https://api.alyf.ai/account/api/v1/accounts/name/phoneNumber';

const sanitizePhoneNumber = (phoneNumber: string) =>
  phoneNumber.replace(/[\s()-]+/g, '').trim();

const MOROCCAN_PHONE_NUMBER_PATTERN = /^(?:\+212|212|0)([67]\d{8})$/;

export const normalizeMoroccanPhoneNumber = (phoneNumber: string): string | null => {
  const sanitizedPhoneNumber = sanitizePhoneNumber(phoneNumber);
  const match = sanitizedPhoneNumber.match(MOROCCAN_PHONE_NUMBER_PATTERN);

  if (!match) {
    return null;
  }

  return `212${match[1]}`;
};

const extractAccountName = (payload: unknown): string | null => {
  if (typeof payload === 'string') {
    const normalized = payload.trim();
    return normalized || null;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const name = extractAccountName(item);
      if (name) {
        return name;
      }
    }

    return null;
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;

  for (const key of ['firstname', 'firstName', 'name', 'fullName', 'accountName', 'displayName']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  for (const key of ['data', 'result', 'account', 'payload']) {
    const nested = extractAccountName(record[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const extractAccountExists = (payload: unknown): boolean => {
  if (typeof payload === 'boolean') {
    return payload;
  }

  if (typeof payload === 'string') {
    return payload.trim().length > 0;
  }

  if (!payload) {
    return false;
  }

  if (typeof payload !== 'object') {
    return true;
  }

  const record = payload as Record<string, unknown>;

  for (const key of ['exists', 'found', 'available']) {
    const value = record[key];
    if (typeof value === 'boolean') {
      return value;
    }
  }

  return Object.keys(record).length > 0;
};

const extractErrorDetail = (payload: unknown): string | null => {
  if (typeof payload === 'string') {
    const normalized = payload.trim();
    return normalized || null;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const detail = extractErrorDetail(item);
      if (detail) {
        return detail;
      }
    }

    return null;
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;

  for (const key of ['message', 'error', 'detail', 'details', 'description', 'title']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  for (const key of ['data', 'payload', 'result', 'errors']) {
    const nested = extractErrorDetail(record[key]);
    if (nested) {
      return nested;
    }
  }

  return null;
};

export const lookupAccountByPhone = async (phoneNumber: string) => {
  const normalizedPhoneNumber = normalizeMoroccanPhoneNumber(phoneNumber);

  if (!normalizedPhoneNumber) {
    throw new Error('Invalid Moroccan phone number.');
  }

  const response = await apiHmacService.get(
    `${ACCOUNT_LOOKUP_BASE_URL}/${encodeURIComponent(normalizedPhoneNumber)}`
  );
  const exists = response.status === 200 && extractAccountExists(response.data);

  return {
    exists,
    normalizedPhoneNumber,
    name: exists ? extractAccountName(response.data) : null,
  };
};

export const isAccountNotFoundError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404;

export const getAccountLookupErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const apiDetail = extractErrorDetail(error.response?.data);

    if (status) {
      return apiDetail
        ? `Erreur API (${status}) : ${apiDetail}`
        : `Erreur API (${status}) : ${error.message}`;
    }

    if (error.code === 'ECONNABORTED') {
      return 'Erreur reseau : delai de reponse depasse.';
    }

    return `Erreur reseau : ${error.message}`;
  }

  if (error instanceof Error) {
    return error.message.trim() ? `Erreur locale : ${error.message}` : 'Erreur locale inconnue.';
  }

  return 'Erreur inconnue.';
};
